import { BerthCallInfo } from '../../../../../types/ViewModel/BerthCallInfo';
import { TankersEvent } from '../../../../../types/ViewModel/TankersEvent';
import { EventTypeEnum } from 'src/enums/eventTypeEnum';
import { ExcelReportInfoBM } from 'src/types/BusinessModel/ExcelReportInfoBM';
import ExcelJS from 'exceljs';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { getFormattedCargoNumber, isLoadingCargoOperation, getChartererName as getChartererNameFromCargo } from 'src/utils/stowage/cargoOperationUtils';
import { setCellValueWithFont } from '../reportEventExcelUtils';
import { getFormattedDate } from 'src/utils/reportGenerator/excelReportUtils';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { prettifyDateTimeHHmm } from 'odfjell-date-time-utils/dateTimeFormatter';
import { isNullOrUndefined } from 'src/utils/objectUtils';

export interface NodeIdMap {
    berthCallId: number;
    cargoOpId: number | null;
    rotationNodeId: string;
    adjustedName: string | null;
    seqId: number | null;
}

const MAX_WORKSHEET_NAME_LENGTH = 31;
const INVALID_WORKSHEET_NAME_CHARS_REGEX = /[:\\/?*[\]]/g;

export const itemExists = (berthCallList: BerthCallInfo[] | null | undefined, id: number | null | undefined): boolean => {
    if (id !== null && id !== undefined && berthCallList && berthCallList.length > 0) {
        return berthCallList.some((bc) => bc.berthCallId === id);
    }
    return false;
};

export const itemExistsInDelimitedString = (
    delimitedList: string | null | undefined,
    value: string | null | undefined,
    delimiter: string
): boolean => {
    if (!value || !delimitedList) return false;
    const items = delimitedList.split(delimiter).map((item) => item.trim());
    return items.includes(value.trim());
};

export const itemExistsInCommaDelimitedString = (bcStr: string | null | undefined, id: number | null | undefined): boolean => {
    if (id !== null && id !== undefined && bcStr && bcStr.trim() !== '') {
        const bcList = bcStr.split(',');
        return bcList.some((o) => o === id.toString());
    }
    return false;
};

export const getTranshipment = (eventList: TankersEvent[]): string =>
    eventList.find((event) => event.eventTypeId === EventTypeEnum.TransShipmentVesselAlongside) ? 'Yes' : 'No';

export const scrubWorksheetName = (name: string, fallback = 'Sheet'): string => {
    const sanitized = name.replace(INVALID_WORKSHEET_NAME_CHARS_REGEX, ' ').replace(/\s+/g, ' ').trim();
    const normalized = sanitized.length > 0 ? sanitized : fallback;
    return normalized.length > MAX_WORKSHEET_NAME_LENGTH ? normalized.substring(0, MAX_WORKSHEET_NAME_LENGTH) : normalized;
};

export const createAdjustedTabName = (name: string): string => scrubWorksheetName(name);

export const getBerthName = (berthName: string, nodeIdMap: NodeIdMap): string => {
    const sanitizedBerthName = scrubWorksheetName(berthName);
    let seqStr = '';
    if (nodeIdMap.seqId !== null && nodeIdMap.seqId !== undefined) {
        seqStr = ` (${nodeIdMap.seqId.toString()})`;
    }
    if (sanitizedBerthName.length + seqStr.length > MAX_WORKSHEET_NAME_LENGTH) {
        return sanitizedBerthName.substring(0, MAX_WORKSHEET_NAME_LENGTH - seqStr.length).trimEnd() + seqStr;
    }
    return sanitizedBerthName + seqStr;
};

export const getImosBerthCallIds = (portCall: RotationPortCall, excelReportInfo: ExcelReportInfoBM) =>
    portCall.berthCalls
        .filter((c) => excelReportInfo.totalBerthCallIdList.some((o) => o.berthCallId == c.imosBerthCallId))
        .map((c) => c.imosBerthCallId);

export const getNumberOfCargoOps = (portCall: RotationPortCall, bcIdList: number[]) =>
    portCall.berthCalls.flatMap((co) => co.cargoOperations.filter((c) => bcIdList.some((o) => o == c.imosCargoOperationId))).length;

export const getBunkersReceived = (pcEvList: TankersEvent[], portCallId: number): string =>
    pcEvList.find((o) => o.portCallId === portCallId && o.eventTypeId === EventTypeEnum.BunkerBargeAlongside) ? 'Yes' : 'No';

export const getImosBerthCallId = (callId: string | null): number => {
    if (!callId) return 0;
    const splitResult = callId.split('-');
    if (splitResult.length === 2) {
        const parsed = parseInt(splitResult[1], 10);
        return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
};

export const getAllFastDate = (evList: TankersEvent[]): Date | null =>
    evList.find((o) => o.eventTypeId === EventTypeEnum.AllFast)?.eventDate?.actualDateTimeLocal || null;

export const getAllClearDate = (evList: TankersEvent[]): Date | null =>
    evList.find((o) => o.eventTypeId === EventTypeEnum.AllClear)?.eventDate?.actualDateTimeLocal || null;

export const getEOSPDate = (evList: TankersEvent[]): Date | null =>
    evList.find((o) => o.eventTypeId === EventTypeEnum.EOSP)?.eventDate?.actualDateTimeLocal || null;

export const getSOSPDate = (evList: TankersEvent[]): Date | null =>
    evList.find((o) => o.eventTypeId === EventTypeEnum.SOSP)?.eventDate?.actualDateTimeLocal || null;

export const getDaysInPort = (eospDate: Date | null, sospDate: Date | null): number | null => {
    if (eospDate && sospDate && sospDate >= eospDate) {
        const timeDiff = sospDate.getTime() - eospDate.getTime();
        return timeDiff / (1000 * 60 * 60 * 24);
    }
    return null;
};

export const getFirstHoseOnDate = (coEvList: TankersEvent[]): Date | null => {
    const hoseOnList = coEvList
        .filter((o) => o.eventTypeId === EventTypeEnum.CargoHoseOn)
        .sort((a, b) => (a.eventDate?.actualDateTimeLocal?.getTime() || 0) - (b.eventDate?.actualDateTimeLocal?.getTime() || 0));
    return hoseOnList[0]?.eventDate?.actualDateTimeLocal || null;
};

export const getLastHoseOffDate = (coEvList: TankersEvent[]): Date | null => {
    const hoseOffList = coEvList
        .filter((o) => o.eventTypeId === EventTypeEnum.CargoHoseOff)
        .sort((a, b) => (b.eventDate?.actualDateTimeLocal?.getTime() || 0) - (a.eventDate?.actualDateTimeLocal?.getTime() || 0));
    return hoseOffList[0]?.eventDate?.actualDateTimeLocal || null;
};

export const getAllowedDaysPumping = (pumpingRate: number | null | undefined, blQuantity: number | null | undefined): number | null =>
    pumpingRate && pumpingRate > 0 && blQuantity !== null && blQuantity !== undefined ? Math.abs(blQuantity / (pumpingRate * 24)) : null;

export const filterCargoOpForCharterer = (portCall: RotationPortCall, coIdList: number[], chartererNameList: string): number[] =>
    coIdList.filter(
        (coId) =>
            portCall.berthCalls.flatMap((bc) =>
                bc.cargoOperations?.some(
                    (co) =>
                        co.imosCargoOperationId === coId &&
                        itemExistsInDelimitedString(chartererNameList, getChartererNameFromCargo(co.cargo)?.trim(), ';')
                )
            ) || false
    );

export const getTotalQuantity = (portCall: RotationPortCall, selectedBerthCallIdList: BerthCallInfo[], bcId?: number | null): number => {
    let bcList: number[] = [];

    if (bcId !== null && bcId !== undefined) {
        bcList = portCall.berthCalls?.filter((c) => c.imosBerthCallId === bcId).map((c) => c.imosBerthCallId) || [];
    } else {
        bcList =
            portCall.berthCalls
                ?.filter((c) => selectedBerthCallIdList.some((o) => o.berthCallId === c.imosBerthCallId))
                .flatMap((c) => c.imosBerthCallId) || [];
    }

    let totalQuantity = 0;
    const coList =
        portCall.berthCalls.flatMap((bc) => bc.cargoOperations?.filter((o) => bcList.some((bc) => bc === o.berthCall?.imosBerthCallId))) || [];

    for (const co of coList) {
        totalQuantity += Math.abs(co.billOfLadingWeight || 0);
    }

    return totalQuantity;
};

export const getBLQuantity = (portCall: RotationPortCall, cargoOperationId: number): number | undefined => {
    const co = portCall.berthCalls.flatMap((co) => co.cargoOperations).find((o) => o.imosCargoOperationId === cargoOperationId);
    return co?.billOfLadingWeight;
};

export const getNorTenderEvent = (evList: TankersEvent[], cargoOpId: number): TankersEvent | null => {
    const norTenderEvents = evList
        .filter(
            (ev) =>
                (ev.eventTypeId === EventTypeEnum.NorTender || ev.eventTypeId === EventTypeEnum.ReTenderNor) &&
                ev.eventTypeInputRegList?.some(
                    (etir) =>
                        etir.eventTypeInputId === 1 &&
                        etir.dropdownGroupReg !== null &&
                        etir.dropdownGroupReg.dropdownItemRegList?.some((dir) => dir.dropdownItemId === cargoOpId)
                )
        )
        .sort((a, b) => (b.eventDate?.actualDateTimeLocal?.getTime() || 0) - (a.eventDate?.actualDateTimeLocal?.getTime() || 0));

    return norTenderEvents.length > 0 ? norTenderEvents[0] : null;
};

export const getCargoOperationFromStowage = (portCall: RotationPortCall, ev: TankersEvent) =>
    portCall.berthCalls.flatMap((bc) => bc.cargoOperations).find((o) => ev.cargoOperationId === o.imosCargoOperationId);

export const getChartererName = (cargoOperation: RotationCargoOperation): string | null => getChartererNameFromCargo(cargoOperation?.cargo) || null;
export const getCargoName = (cargoOperation: RotationCargoOperation): string => cargoOperation?.cargo?.product?.tradeName || '';
export const getCargoNumber = (cargoOperation: RotationCargoOperation): string =>
    getFormattedCargoNumber(cargoOperation.cargo.voyageNumber, cargoOperation.cargo.cargoNumber, false);

export const copySheet = (workbook: ExcelJS.Workbook, templateSheet: ExcelJS.Worksheet, newSheetName: string): ExcelJS.Worksheet => {
    const newSheet = workbook.addWorksheet(scrubWorksheetName(newSheetName));

    templateSheet.eachRow((row, rowNumber) => {
        const newRow = newSheet.getRow(rowNumber);
        row.eachCell((cell, colNumber) => {
            const newCell = newRow.getCell(colNumber);
            newCell.value = cell.value;
            newCell.style = { ...cell.style };
        });
        newRow.height = row.height;
    });

    // Copy column widths safely (templateSheet.columns can be null/undefined)
    const colCount = templateSheet.columnCount || 0;
    for (let i = 1; i <= colCount; i++) {
        const srcCol = templateSheet.getColumn(i);
        const width = (srcCol as any)?.width as number | undefined;
        if (typeof width === 'number') {
            newSheet.getColumn(i).width = width;
        }
    }

    return newSheet;
};

export const addVesselDetails = (
    worksheet: ExcelJS.Worksheet,
    rowNr: number,
    firstCol: number,
    secondCol: number,
    voyageNr: string | null | undefined,
    portName: string | null | undefined,
    vessel: Vessel | null | undefined,
    cargoNr: string | null | undefined,
    dt?: Date | null
) => {
    let row = rowNr;

    const write = (
        r: number,
        c: number,
        label: string,
        value?: string | null,
        opts?: { includeColon?: boolean; labelAlign?: 'left' | 'center' | 'right' }
    ) => {
        const v = value?.trim();
        if (!v) return;
        const includeColon = opts?.includeColon !== false; // default true
        setCellValueWithFont(worksheet, r, c, label, opts?.labelAlign ? { align: opts.labelAlign } : undefined);
        if (includeColon) {
            setCellValueWithFont(worksheet, r, c + 1, ':');
            setCellValueWithFont(worksheet, r, c + 2, v, { mergeAcross: 2, align: 'left' });
        } else {
            // No colon: start value at c+1; clamp merge so it doesn't exceed column 9 (I)
            const start = c + 1;
            const maxAcross = Math.max(1, Math.min(3, 9 - start + 1));
            setCellValueWithFont(worksheet, r, start, v, { mergeAcross: maxAcross, align: 'left' });
        }
    };

    // Row 1
    write(row, firstCol, 'Vessel', vessel?.vesselName);
    write(row, secondCol, 'Voyage:', voyageNr, { includeColon: false, labelAlign: 'right' });
    row++;

    // Row 2
    write(row, firstCol, 'Port', portName);
    write(row, secondCol, 'Cargo No:', cargoNr, { includeColon: false, labelAlign: 'right' });

    // Row 3 (date, left side only)
    if (dt) write(row + 1, firstCol, 'Date', getFormattedDate(dt));

    // Add a border around the entire region (from rowNr to last row, and from firstCol to secondCol+3)
    const startRow = rowNr;
    const endRow = dt ? row + 1 : row; // if date row was written, it's one below current 'row'
    const startCol = firstCol;
    const endCol = Math.min(secondCol + 3, 9); // cap at column I (9)

    const edge = { style: 'thin' as ExcelJS.BorderStyle };
    // Top and bottom borders
    for (let c = startCol; c <= endCol; c++) {
        const topCell = worksheet.getCell(startRow, c);
        topCell.border = { ...(topCell.border || {}), top: edge } as ExcelJS.Borders;
        const bottomCell = worksheet.getCell(endRow, c);
        bottomCell.border = { ...(bottomCell.border || {}), bottom: edge } as ExcelJS.Borders;
    }
    // Left and right borders
    for (let r = startRow; r <= endRow; r++) {
        const leftCell = worksheet.getCell(r, startCol);
        leftCell.border = { ...(leftCell.border || {}), left: edge } as ExcelJS.Borders;
        const rightCell = worksheet.getCell(r, endCol);
        rightCell.border = { ...(rightCell.border || {}), right: edge } as ExcelJS.Borders;
    }

    return row++;
};

/**
 * Returns "load"/"discharge" or "loading"/"discharging"; uppercases when requested.
 */
export const replaceLoadDischargePhrase = (
    cargoOperation: RotationCargoOperation,
    toUpper: boolean,
    templateWordEnding: 'default' | 'ing',
    isLoading: boolean = isLoadingCargoOperation(cargoOperation)
): string => {
    const word = templateWordEnding === 'ing' ? (isLoading ? 'loading' : 'discharging') : isLoading ? 'load' : 'discharge';
    return toUpper ? word.toUpperCase() : word;
};

// C# equivalent of IsCargoNumberEqual
// private static bool IsCargoNumberEqual(int drDownItem, string externalId)
export const isCargoNumberEqual = (drDownItem: number, externalId: string | null | undefined): boolean => {
    if (externalId && externalId.length > 0) {
        return drDownItem.toString() === externalId;
    }
    return false;
};

// C# equivalent of IsCargoOpIncluded
// private static bool IsCargoOpIncluded(Event ev, string externalId, int cargoDropdownInpId)
export const isCargoOpIncluded = (ev: TankersEvent, externalId: string | null | undefined, cargoDropdownInpId: number): boolean => {
    const coDrInput = ev.eventTypeInputRegList?.find((o) => o.eventTypeInputId === cargoDropdownInpId);
    const item = coDrInput?.dropdownGroupReg?.dropdownItemRegList?.find((o) => isCargoNumberEqual(o.dropdownItemId, externalId));
    return !!item;
};

// C# equivalent of GetCargoOperationsFromStowage
// private static List<CargoOperation> GetCargoOperationsFromStowage(StowagePlan stowagePlan, Event ev, int cargoDropdownInpId)
export const getCargoOperations = (
    cargoOperations: RotationCargoOperation[],
    ev: TankersEvent,
    cargoDropdownInpId: number
): RotationCargoOperation[] => {
    return cargoOperations.filter((o) => isCargoOpIncluded(ev, o.imosCargoOperationId.toString(), cargoDropdownInpId));
};

export const getCargoOperationForEvent = (cargoOperations: RotationCargoOperation[], ev: TankersEvent): RotationCargoOperation => {
    return cargoOperations.find((co) => co.imosCargoOperationId === ev.cargoOperationId);
};
export const getStringValueRegFromEvent = (event: TankersEvent, eventTypeInputId: number) =>
    event.eventTypeInputRegList.find((o) => o.eventTypeInputId == eventTypeInputId)?.stringValueReg;

export const getIntValueRegFromEvent = (event: TankersEvent, eventTypeInputId: number) =>
    event.eventTypeInputRegList.find((o) => o.eventTypeInputId == eventTypeInputId)?.intValueReg;

export const getDecimalValueRegFromEvent = (event: TankersEvent, eventTypeInputId: number) =>
    event.eventTypeInputRegList.find((o) => o.eventTypeInputId == eventTypeInputId)?.decimalValueReg;

export const getIntValueStrRegFromEvent = (event: TankersEvent, eventTypeInputId: number) => {
    const inputNumber = getIntValueRegFromEvent(event, eventTypeInputId);
    return !isNullOrUndefined(inputNumber) ? inputNumber.toString() : 'N/A';
};
export const getDecimalValueStrRegFromEvent = (event: TankersEvent, eventTypeInputId: number) => {
    const inputNumber = getDecimalValueRegFromEvent(event, eventTypeInputId);
    return !isNullOrUndefined(inputNumber) ? inputNumber.toString() : 'N/A';
};
export const getFirstDropdownValueStr = (event: TankersEvent, eventTypeInputId: number): string =>
    event.eventTypeInputRegList.find((o) => o.eventTypeInputId == eventTypeInputId)?.dropdownGroupReg?.dropdownItemRegList[0]?.dropdownItemValue ||
    'N/A';
export const getWeightStr = (co: RotationCargoOperation, includeMt: boolean): string => {
    const bol = co.billOfLadingWeight;
    const nom = co.nominatedWeight;

    let w: number | null = null;
    if (typeof bol === 'number' && bol > 0) w = bol;
    else if (typeof nom === 'number') w = nom;

    if (w != null) {
        return `${Math.abs(w).toFixed(3)}${includeMt ? 'Mt' : ''}`;
    }
    return 'N/A';
};

export const getTankListStrFromEvent = (event: TankersEvent, eventTypeInputId: number): string => {
    if (!event?.eventTypeInputRegList?.length) return 'N/A';
    const tankListDropdown = event.eventTypeInputRegList.find((o) => o.eventTypeInputId === eventTypeInputId);
    let tankListStr = '';

    if (tankListDropdown?.dropdownGroupReg.dropdownItemRegList.length) {
        tankListStr = (tankListDropdown?.dropdownGroupReg?.dropdownItemRegList ?? []).map((item) => item.dropdownItemValue).join(', ');
    }

    return tankListStr;
};

export const addChartererAndDateTime = (
    worksheet: ExcelJS.Worksheet,
    rowNumber: number,
    cargoOperation: RotationCargoOperation,
    event: TankersEvent,
    signatory: string
) => {
    // Shared styles
    const labelStyle = { mergeAcross: 3, align: 'left' } as const;
    const colonStyle = { align: 'center' } as const;
    const valueBase = { mergeAcross: 4, align: 'left', border: { bottom: { style: 'dotted' as const } }, wrapText: true } as const;

    // Helper to render one labeled row with a colon and underlined value
    const writeRow = (label: string, value: string, boldValue = false) => {
        setCellValueWithFont(worksheet, rowNumber, 1, label, labelStyle as any);
        setCellValueWithFont(worksheet, rowNumber, 5, ':', colonStyle as any);
        setCellValueWithFont(worksheet, rowNumber, 6, value, (boldValue ? { ...valueBase, bold: true } : valueBase) as any);
        rowNumber++;
    };

    writeRow('TENDERED TO', `${getChartererName(cargoOperation)}`, true);
    writeRow('PER', '', true);
    writeRow('DATE AND HOUR', `${prettifyDateTimeHHmm(event.eventDate.actualDateTimeLocal)}`, true);
    writeRow('RECEIVED BY', '');
    writeRow('PER', '');
    writeRow('DATE AND HOUR', '');

    rowNumber += 2;
    setCellValueWithFont(worksheet, rowNumber, 8, 'Yours truly', { align: 'center' });
    rowNumber += 2;
    setCellValueWithFont(worksheet, rowNumber, 7, ``, {
        mergeAcross: 3,
        align: 'left',
        border: { bottom: { style: 'dotted' } },
        wrapText: true,
    });
    rowNumber += 2;
    setCellValueWithFont(worksheet, rowNumber, 8, signatory, { align: 'center' });
    return rowNumber + 1;
};

export const odfjellHeaderColor = '#000080';
export const addOdfjellHeader = (worksheet: ExcelJS.Worksheet, logoImageId: number) => {
    const rowNum = 3;
    setCellValueWithFont(worksheet, rowNum, 1, 'ODFJELL TANKERS', { bold: true, color: odfjellHeaderColor, fontSize: 18 });
    worksheet.mergeCells(rowNum, 1, rowNum, 5);
    worksheet.addImage(logoImageId, 'H3:I4');
    return rowNum;
};

export const addCargoAndBerthDescription = (worksheet: ExcelJS.Worksheet, cargoOperation: RotationCargoOperation, rowNum: number) => {
    rowNum++;
    const rows = [
        { label: 'Cargo', value: getCargoName(cargoOperation) },
        { label: 'Berth/Terminal', value: cargoOperation.berthCall?.berthName ?? 'Unknown' },
    ];

    rows.forEach((r, idx) => {
        setCellValueWithFont(worksheet, rowNum, 1, r.label, { mergeAcross: 3, align: 'left' });
        setCellValueWithFont(worksheet, rowNum, 4, r.value, { align: 'left' });
        if (idx === 0) rowNum++;
    });

    return rowNum++;
};
